/*
  ЦИФРОВОЙ ДИАК ЗФ — EDITABLE GANTT CHART
  План-график проекта: одна горизонтальная прокрутка, сетка месяцев/недель,
  полосы в px/день, строки по дате начала.
 */

import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  ExternalLink,
  Loader2,
  Package,
} from "lucide-react";
import { toast } from "sonner";
import {
  generateId,
  type EditableGanttTask,
  type ProjectDocument,
  type ProjectDocumentVersion,
  compareGanttTasksDisplayOrder,
  DOC_GROUP_WORKING,
  DOC_TYPE_GANTT_DELIVERABLE,
} from "@/store/projectStore";
import { projectFileUrl, uploadProjectDocumentFile, deleteProjectFileOnServer } from "@/lib/api";
import { useProjects } from "@/contexts/ProjectContext";
import { InlineText, InlineDate, InlineSelect, InlineNumber } from "./InlineEdit";
import { AssigneeTokens } from "./AssigneeTokens";
import { BarAssigneesStack } from "./BarAssigneesStack";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  formatRuDateRange,
  tasksOutsideRoadmapEnvelope,
  type DateEnvelope,
} from "@/lib/mainGanttProjectSpan";
import {
  PROJECT_GANTT_PX_PER_DAY,
  buildProjectGanttTimeline,
  taskBarGeometry,
  addCalendarDays,
  localTodayIso,
  isoToLocalDate,
  type ProjectGanttTimelineModel,
} from "@/lib/ganttTimeline";

const STATUS_OPTIONS = [
  { value: "active", label: "В работе" },
  { value: "behind", label: "Сдвиг сроков" },
  { value: "planned", label: "Запланировано" },
  { value: "done", label: "Выполнено" },
] as const;

const STATUS_COLORS: Record<EditableGanttTask["status"], string> = {
  active: "#00A651",
  /** Заметный, но не «просрочка как молоко» — отклонение от плана */
  behind: "#E85D04",
  planned: "#0055A5",
  done: "#8B9BB4",
};

const STATUS_LABELS: Record<EditableGanttTask["status"], string> = {
  active: "В работе",
  behind: "Сдвиг сроков",
  planned: "Запланировано",
  done: "Выполнено",
};

const LEFT_COL = "w-[15.5rem] min-w-[15.5rem] shrink-0";

const FILE_INPUT_ACCEPT =
  ".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,image/png,image/jpeg,image/webp";

function formatArtifactBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 1024) return `${n} Б`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10_240 ? 1 : 0)} КБ`;
  return `${(n / (1024 * 1024)).toFixed(1)} МБ`;
}

function GanttTaskDeliverablesSection({
  projectId,
  task,
  documents,
  onDocumentsChange,
  readOnly,
}: {
  projectId: string;
  task: EditableGanttTask;
  documents: ProjectDocument[];
  onDocumentsChange: (next: ProjectDocument[]) => void;
  readOnly: boolean;
}) {
  const taskDocs = useMemo(() => {
    const list = documents.filter((d) => d.sourceGanttTaskId === task.id);
    return [...list].sort((a, b) => {
      const ta = a.versions[0]?.uploadedAt ?? "";
      const tb = b.versions[0]?.uploadedAt ?? "";
      return new Date(tb).getTime() - new Date(ta).getTime();
    });
  }, [documents, task.id]);

  const newInputRef = useRef<HTMLInputElement>(null);
  const verInputRef = useRef<HTMLInputElement>(null);
  const pendingVersionDocIdRef = useRef<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyVersionDocId, setBusyVersionDocId] = useState<string | null>(null);

  const canAttach = task.status === "done";

  const runUpload = async (file: File | undefined, mode: { type: "new" } | { type: "version"; documentId: string }) => {
    if (!file || busy || readOnly) return;
    setBusy(true);
    if (mode.type === "version") setBusyVersionDocId(mode.documentId);
    try {
      const version = await uploadProjectDocumentFile(projectId, file);
      if (mode.type === "new") {
        const docId = generateId("doc");
        const baseTitle = file.name.replace(/\.[^.]+$/, "").trim() || "Артефакт";
        onDocumentsChange([
          ...documents,
          {
            id: docId,
            title: baseTitle,
            groupId: DOC_GROUP_WORKING,
            typeId: DOC_TYPE_GANTT_DELIVERABLE,
            sourceGanttTaskId: task.id,
            versions: [version],
          },
        ]);
        toast.success("Артефакт добавлен и отображается в «Документах проекта»");
      } else {
        onDocumentsChange(
          documents.map((d) =>
            d.id === mode.documentId ? { ...d, versions: [version, ...d.versions] } : d,
          ),
        );
        toast.success("Новая версия загружена");
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось загрузить файл");
    } finally {
      setBusy(false);
      setBusyVersionDocId(null);
      if (newInputRef.current) newInputRef.current.value = "";
      if (verInputRef.current) verInputRef.current.value = "";
    }
  };

  const removeVersionFromDoc = async (doc: ProjectDocument, version: ProjectDocumentVersion) => {
    if (readOnly) return;
    const rest = doc.versions.filter((v) => v.id !== version.id);
    try {
      await deleteProjectFileOnServer(projectId, version.id);
    } catch {
      toast.error("Файл на сервере не удалён (возможно, уже отсутствует)");
    }
    if (rest.length === 0) {
      onDocumentsChange(documents.filter((d) => d.id !== doc.id));
    } else {
      onDocumentsChange(documents.map((d) => (d.id === doc.id ? { ...d, versions: rest } : d)));
    }
    toast.success("Версия удалена");
  };

  return (
    <div className="space-y-2 border-t border-border/80 pt-3 sm:col-span-2">
      <div className="flex items-start gap-2">
        <Package size={14} className="mt-0.5 shrink-0 text-muted-foreground" strokeWidth={2} />
        <div className="min-w-0 flex-1">
          <span className="text-xs font-medium text-foreground">Артефакты выполнения</span>
          <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
            После статуса «Выполнено» прикрепите инкремент или иной отчётный файл — он попадёт в раздел «Документы
            проекта» (категория «Артефакт задачи»).
          </p>
        </div>
      </div>

      <input
        ref={newInputRef}
        type="file"
        className="sr-only"
        accept={FILE_INPUT_ACCEPT}
        onChange={(e) => {
          const f = e.target.files?.[0];
          void runUpload(f, { type: "new" });
        }}
      />
      <input
        ref={verInputRef}
        type="file"
        className="sr-only"
        accept={FILE_INPUT_ACCEPT}
        onChange={(e) => {
          const f = e.target.files?.[0];
          const docId = pendingVersionDocIdRef.current;
          pendingVersionDocIdRef.current = null;
          if (docId && f) void runUpload(f, { type: "version", documentId: docId });
        }}
      />

      {taskDocs.length > 0 ? (
        <ul className="space-y-2 rounded-lg border border-border/80 bg-background/50 p-2">
          {taskDocs.map((doc) => {
            const latest = doc.versions[0];
            if (!latest) return null;
            return (
              <li
                key={doc.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/60 bg-card/80 px-2 py-1.5 text-[11px]"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-foreground">{doc.title}</div>
                  <div className="truncate text-muted-foreground">
                    {latest.originalName} · {formatArtifactBytes(latest.size)}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <a
                    href={projectFileUrl(projectId, latest.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 rounded border border-border px-1.5 py-0.5 text-[10px] font-medium text-[var(--nn-blue)] hover:bg-muted/50"
                  >
                    <ExternalLink size={10} />
                    Открыть
                  </a>
                  {!readOnly && doc.versions.length > 0 ? (
                    <button
                      type="button"
                      disabled={busy}
                      className="rounded px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-muted disabled:opacity-50"
                      onClick={() => {
                        pendingVersionDocIdRef.current = doc.id;
                        verInputRef.current?.click();
                      }}
                    >
                      {busy && busyVersionDocId === doc.id ? <Loader2 size={10} className="animate-spin" /> : null}
                      Версия
                    </button>
                  ) : null}
                  {!readOnly ? (
                    <button
                      type="button"
                      className="rounded p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      aria-label="Удалить документ"
                      onClick={() => void removeVersionFromDoc(doc, latest)}
                    >
                      <Trash2 size={12} />
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-[11px] italic text-muted-foreground">Пока нет прикреплённых артефактов.</p>
      )}

      {!readOnly ? (
        <button
          type="button"
          disabled={busy || !canAttach}
          onClick={() => newInputRef.current?.click()}
          title={
            canAttach
              ? "Загрузить файл"
              : "Сначала установите статус задачи «Выполнено», чтобы прикрепить артефакт"
          }
          className={cn(
            "inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed py-2 text-[11px] font-medium transition-colors sm:w-auto sm:px-3",
            canAttach
              ? "border-border text-muted-foreground hover:border-[var(--nn-blue)] hover:bg-[var(--nn-blue-pale)]/30 hover:text-[var(--nn-blue)] disabled:opacity-50"
              : "cursor-not-allowed border-border/60 text-muted-foreground/60",
          )}
        >
          {busy && !busyVersionDocId ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
          Прикрепить артефакт
        </button>
      ) : null}
    </div>
  );
}

function TimelineGrid({
  model,
  className,
  children,
}: {
  model: ProjectGanttTimelineModel;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className={cn("relative", className)} style={{ width: model.widthPx }}>
      {model.weekLinePx.map((x, i) => (
        <div
          key={`w-${i}`}
          className="pointer-events-none absolute top-0 bottom-0 w-px bg-border/30"
          style={{ left: x }}
        />
      ))}
      {model.monthLinePx.map((x, i) => (
        <div
          key={`m-${i}`}
          className="pointer-events-none absolute top-0 bottom-0 w-px bg-border/70"
          style={{ left: x }}
        />
      ))}
      {model.todayLinePx != null ? (
        <div
          className="pointer-events-none absolute top-0 bottom-0 z-[5] w-0.5 bg-rose-500/50"
          style={{ left: model.todayLinePx }}
        />
      ) : null}
      {children}
    </div>
  );
}

function SortableGanttGridRow({
  task,
  index,
  model,
  expandedId,
  setExpandedId,
  onUpdate,
  onDelete,
  readOnly,
}: {
  task: EditableGanttTask;
  index: number;
  model: ProjectGanttTimelineModel;
  expandedId: string | null;
  setExpandedId: React.Dispatch<React.SetStateAction<string | null>>;
  onUpdate: (taskId: string, patch: Partial<EditableGanttTask>) => void;
  onDelete: (taskId: string) => void;
  readOnly: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    disabled: readOnly,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    gridColumn: "1 / -1" as const,
    display: "grid",
    gridTemplateColumns: "subgrid",
  };

  const bar = taskBarGeometry(task, model);
  const color = STATUS_COLORS[task.status];
  const zebra = index % 2 === 1;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group/gantt-row border-b border-border",
        isDragging && "relative z-20 bg-background/95 shadow-md ring-1 ring-border",
      )}
    >
      <div
        className={cn(
          "sticky left-0 z-[14] box-border flex h-12 max-h-12 shrink-0 items-stretch overflow-hidden border-r border-border",
          "bg-background shadow-[4px_0_8px_-4px_rgba(0,0,0,0.07)]",
          zebra && "bg-muted",
        )}
      >
        <div className="flex min-h-0 min-w-0 flex-1 items-center gap-px px-1 py-0.5 pr-1.5">
          {!readOnly ? (
            <button
              type="button"
              className="flex h-7 w-5 shrink-0 cursor-grab touch-none items-center justify-center rounded p-0.5 text-muted-foreground hover:bg-muted active:cursor-grabbing"
              aria-label="Перетащить строку"
              title="Перетащить"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          ) : (
            <span className="w-5 shrink-0" aria-hidden />
          )}
          <button
            type="button"
            onClick={() => setExpandedId((id) => (id === task.id ? null : task.id))}
            className="flex h-6 w-5 shrink-0 items-center justify-center rounded p-0.5 hover:bg-muted"
            aria-expanded={expandedId === task.id}
            aria-label={
              expandedId === task.id
                ? "Закрыть панель параметров задачи"
                : "Открыть параметры задачи в боковой панели"
            }
            title={expandedId === task.id ? "Закрыть" : "Параметры, статус, удаление"}
          >
            {expandedId === task.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
          {!readOnly ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(task.id);
              }}
              className="flex h-6 w-5 shrink-0 items-center justify-center rounded p-0.5 text-muted-foreground/35 opacity-0 transition-[opacity,color] hover:bg-red-50 hover:text-red-600 group-hover/gantt-row:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
              aria-label="Удалить задачу"
              title="Удалить задачу"
            >
              <Trash2 size={12} strokeWidth={2} />
            </button>
          ) : (
            <span className="w-5 shrink-0" aria-hidden />
          )}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col justify-center gap-0 leading-tight">
            <InlineText
              value={task.title}
              onSave={(v) => onUpdate(task.id, { title: v })}
              className="text-[13px] font-medium leading-tight"
              clampLines={2}
              displayAs="span"
              readOnly={readOnly}
            />
            <p className="shrink-0 truncate text-[10px] leading-tight text-muted-foreground">
              {formatRuDateRange(task.start, task.end)}
            </p>
          </div>
        </div>
      </div>

      <div
        className={cn(
          "relative z-0 min-w-0 box-border h-12 max-h-12 w-full shrink-0 overflow-hidden",
          zebra && "bg-muted/15",
        )}
      >
        <TimelineGrid model={model} className="pointer-events-none absolute left-0 top-0 h-full" />
        {bar ? (
          <div
            className="absolute top-1/2 z-[6] h-5 max-h-5 -translate-y-1/2"
            style={{
              left: bar.leftPx,
              width: bar.widthPx,
            }}
          >
            <div
              className="absolute inset-x-0 top-1/2 h-4 -translate-y-1/2 overflow-hidden rounded-sm shadow-sm"
              style={{ backgroundColor: color }}
              title={`${task.start} → ${task.end}`}
            >
              {task.progress > 0 ? (
                <div className="h-full rounded-sm bg-black/25" style={{ width: `${task.progress}%` }} />
              ) : null}
            </div>
            {task.assignees.length > 0 ? (
              <BarAssigneesStack assignees={task.assignees} barWidthPx={bar.widthPx} />
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function TaskExpandedPanel({
  task,
  projectId,
  documents,
  onDocumentsChange,
  onUpdate,
  onDeleteRow,
  readOnly: ro,
  className,
  popoverPortalContainer,
}: {
  task: EditableGanttTask;
  projectId: string;
  documents: ProjectDocument[];
  onDocumentsChange: (next: ProjectDocument[]) => void;
  onUpdate: (patch: Partial<EditableGanttTask>) => void;
  /** Удобно на тач-экранах, где нет hover у корзины в строке */
  onDeleteRow?: () => void;
  readOnly: boolean;
  /** Доп. классы корня (например, без верхней границы в sheet) */
  className?: string;
  popoverPortalContainer?: HTMLElement | null;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-1 border-t border-border bg-muted/10 px-4 py-3 sm:grid-cols-2 sm:gap-4",
        className,
      )}
    >
      <div className="space-y-2 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Статус</span>
          <InlineSelect
            value={task.status}
            options={STATUS_OPTIONS as unknown as { value: EditableGanttTask["status"]; label: string }[]}
            onSave={(v) => onUpdate({ status: v })}
            readOnly={ro}
            renderValue={(v) => (
              <span
                className="rounded-full px-2 py-0.5 text-xs font-semibold"
                style={{ backgroundColor: STATUS_COLORS[v] + "22", color: STATUS_COLORS[v] }}
              >
                {STATUS_LABELS[v]}
              </span>
            )}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <InlineDate value={task.start} onSave={(v) => onUpdate({ start: v })} label="Начало" readOnly={ro} />
          <InlineDate value={task.end} onSave={(v) => onUpdate({ end: v })} label="Конец" readOnly={ro} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Прогресс:</span>
          <InlineNumber
            value={task.progress}
            onSave={(v) => onUpdate({ progress: v })}
            min={0}
            max={100}
            suffix="%"
            readOnly={ro}
          />
        </div>
        <div className="flex items-start gap-2">
          <span className="mt-1 text-xs text-muted-foreground">Исполнители:</span>
          <div className="min-w-0 flex-1">
            <AssigneeTokens
              value={task.assignees}
              onChange={(next) => onUpdate({ assignees: next })}
              readOnly={ro}
              placeholder="ФИО или e-mail"
              popoverPortalContainer={popoverPortalContainer}
            />
          </div>
        </div>
      </div>
      <div className="mt-3 space-y-2 sm:mt-0">
        <div>
          <span className="mb-1 block text-xs text-muted-foreground">Описание:</span>
          <InlineText
            value={task.description}
            onSave={(v) => onUpdate({ description: v })}
            multiline
            placeholder="Описание задачи…"
            className="w-full text-sm"
            readOnly={ro}
          />
        </div>
      </div>

      <GanttTaskDeliverablesSection
        projectId={projectId}
        task={task}
        documents={documents}
        onDocumentsChange={onDocumentsChange}
        readOnly={ro}
      />

      {!ro && onDeleteRow ? (
        <div className="border-t border-border/80 pt-3 sm:col-span-2">
          <button
            type="button"
            onClick={onDeleteRow}
            className="inline-flex items-center gap-1.5 rounded-md border border-red-200/90 bg-red-50/50 px-2.5 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-100 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300 dark:hover:bg-red-950/50"
          >
            <Trash2 size={13} strokeWidth={2} />
            Удалить задачу
          </button>
        </div>
      ) : null}
    </div>
  );
}

interface EditableGanttProps {
  projectId: string;
  tasks: EditableGanttTask[];
  documents: ProjectDocument[];
  onDocumentsChange: (documents: ProjectDocument[]) => void;
  onAdd: (task: Omit<EditableGanttTask, "id">) => void;
  onUpdate: (taskId: string, patch: Partial<EditableGanttTask>) => void;
  onDelete: (taskId: string) => void;
  /** После перетаскивания строки: вставить `activeTaskId` на место `overTaskId`. */
  onReorderTasksByDrag?: (activeTaskId: string, overTaskId: string) => void;
  roadmapEnvelope?: DateEnvelope | null;
  mainGanttBarCount?: number;
  /**
   * `"page"` — полный вид на странице проекта (с алертом дорожной карты, кнопкой «Добавить задачу» и легендой).
   * `"embedded"` — компактный вид для встраивания, например, в раскрытии строки общего Ганта.
   */
  variant?: "page" | "embedded";
}

export function EditableGantt({
  projectId,
  tasks,
  documents,
  onDocumentsChange,
  onAdd,
  onUpdate,
  onDelete,
  onReorderTasksByDrag,
  roadmapEnvelope = null,
  mainGanttBarCount = 0,
  variant = "page",
}: EditableGanttProps) {
  const embedded = variant === "embedded";
  const { canEdit } = useProjects();
  const ro = !canEdit;
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [taskSheetScrollEl, setTaskSheetScrollEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (expandedId && !tasks.some((t) => t.id === expandedId)) setExpandedId(null);
  }, [tasks, expandedId]);

  // Deep-link: ?task=<id> (из уведомлений) — открыть панель параметров.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const target = params.get("task");
    if (!target) return;
    if (!tasks.some((t) => t.id === target)) return;
    setExpandedId(target);
    // Убираем параметр из адресной строки, чтобы при повторной навигации не открывался.
    params.delete("task");
    const next = `${window.location.pathname}${
      params.toString() ? `?${params.toString()}` : ""
    }${window.location.hash}`;
    window.history.replaceState(null, "", next);
  }, [tasks]);

  const tasksOutsideRoadmap = useMemo(() => {
    if (!roadmapEnvelope || tasks.length === 0) return [];
    return tasksOutsideRoadmapEnvelope(roadmapEnvelope, tasks);
  }, [roadmapEnvelope, tasks]);

  const sortedTasks = useMemo(() => [...tasks].sort(compareGanttTasksDisplayOrder), [tasks]);

  const confirmDeleteTask = useCallback(
    (taskId: string) => {
      const t = sortedTasks.find((x) => x.id === taskId);
      const title = (t?.title || "").trim() || "эту задачу";
      if (typeof window !== "undefined") {
        const ok = window.confirm(
          `Удалить задачу «${title}»?\n\nСтрока Ганта и связанные даты/результаты будут удалены. Действие необратимо.`,
        );
        if (!ok) return;
      }
      onDelete(taskId);
    },
    [sortedTasks, onDelete],
  );

  const trackRef = useRef<HTMLDivElement>(null);
  const [trackWidth, setTrackWidth] = useState(0);

  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const resizeObs = new ResizeObserver(() => setTrackWidth(el.clientWidth));
    resizeObs.observe(el);
    setTrackWidth(el.clientWidth);
    return () => resizeObs.disconnect();
  }, [sortedTasks.length]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const sortableTaskIds = useMemo(() => sortedTasks.map((t) => t.id), [sortedTasks]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || !onReorderTasksByDrag || active.id === over.id) return;
      onReorderTasksByDrag(String(active.id), String(over.id));
    },
    [onReorderTasksByDrag],
  );

  const model = useMemo(() => {
    const base = buildProjectGanttTimeline(sortedTasks);
    if (trackWidth <= 0) return base;
    const stretchedPx = Math.max(
      PROJECT_GANTT_PX_PER_DAY,
      Math.ceil(trackWidth / Math.max(1, base.totalDays)),
    );
    if (stretchedPx === base.pxPerDay) return base;
    return buildProjectGanttTimeline(sortedTasks, stretchedPx);
  }, [sortedTasks, trackWidth]);

  const expandedTask = expandedId ? sortedTasks.find((t) => t.id === expandedId) : null;

  const [taskSheetSide, setTaskSheetSide] = useState<"right" | "bottom">("right");
  useLayoutEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const sync = () => setTaskSheetSide(mq.matches ? "right" : "bottom");
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const handleAdd = () => {
    const ordered = [...tasks].sort(compareGanttTasksDisplayOrder);
    const last = ordered[ordered.length - 1];
    const start = last?.start ?? localTodayIso();
    const startD = isoToLocalDate(start) ?? new Date();
    const endD = addCalendarDays(startD, 30);
    const y = endD.getFullYear();
    const m = String(endD.getMonth() + 1).padStart(2, "0");
    const d = String(endD.getDate()).padStart(2, "0");
    const planOrder = ordered.length;
    onAdd({
      title: "Новая задача",
      start,
      end: `${y}-${m}-${d}`,
      status: "planned",
      assignees: [],
      description: "",
      progress: 0,
      planOrder,
    });
  };

  return (
    <div className="w-full min-w-0">
      {!embedded && roadmapEnvelope && tasksOutsideRoadmap.length > 0 ? (
        <Alert className="mb-3 border-amber-200/90 bg-amber-50/60 text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/25 dark:text-amber-50">
          <AlertTriangle className="text-amber-600 dark:text-amber-400" />
          <AlertTitle>Есть задачи за пределами сроков общей дорожной карты</AlertTitle>
          <AlertDescription className="text-amber-900/85 dark:text-amber-100/90">
            <p>
              На главной дорожной карте для этой инициативы задан период{" "}
              <strong>{formatRuDateRange(roadmapEnvelope.start, roadmapEnvelope.end)}</strong>
              {mainGanttBarCount > 1 ? " (несколько полос объединены в один интервал)" : ""}. Следующие задачи
              начинаются раньше или заканчиваются позже этих границ:
            </p>
            <ul className="mt-2 list-disc space-y-0.5 pl-4">
              {tasksOutsideRoadmap.slice(0, 5).map((t) => (
                <li key={t.id}>{t.title}</li>
              ))}
            </ul>
            {tasksOutsideRoadmap.length > 5 ? (
              <p className="mt-1.5">и ещё {tasksOutsideRoadmap.length - 5}…</p>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="w-full min-w-0 overflow-hidden rounded-md border border-border">
        {sortedTasks.length === 0 ? (
          <div className="flex w-full min-w-0 items-stretch">
            <div className={cn(LEFT_COL, "flex flex-col border-r border-border bg-background")}>
              <div className="flex h-7 shrink-0 items-center border-b border-border px-2 text-[10px] font-medium text-muted-foreground">
                Задача и сроки
              </div>
              <div className="flex min-h-12 flex-1 items-center px-2 text-xs text-muted-foreground">—</div>
            </div>
            <div
              ref={trackRef}
              className="flex min-h-12 min-w-0 flex-1 items-center justify-center overflow-x-auto overscroll-x-contain bg-muted/10 py-6 text-center text-sm text-muted-foreground"
            >
              Задач пока нет. Нажмите «Добавить задачу».
            </div>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div
              ref={trackRef}
              className="w-full min-w-0 overflow-x-auto overscroll-x-contain"
            >
              <div
                className="grid"
                style={{
                  gridTemplateColumns: `15.5rem ${model.widthPx}px`,
                }}
              >
                <div className="sticky left-0 z-[16] flex h-7 items-center gap-px border-b border-r border-border bg-background pl-1 pr-2 text-[10px] font-medium text-muted-foreground shadow-[4px_0_8px_-4px_rgba(0,0,0,0.07)]">
                  <span className="w-5 shrink-0" aria-hidden />
                  <span className="w-5 shrink-0" aria-hidden />
                  <span className="w-5 shrink-0" aria-hidden />
                  <span className="min-w-0 flex-1 truncate pl-0.5">Задача и сроки</span>
                </div>
                <div className="relative z-0 h-7 shrink-0 border-b border-border bg-muted/30">
                  <TimelineGrid model={model} className="pointer-events-none absolute left-0 top-0 z-0 h-7">
                    {model.monthLabels.map((m, i) => (
                      <span
                        key={i}
                        className="pointer-events-none absolute top-1/2 z-[1] max-w-[min(8rem,calc(100%-1rem))] -translate-y-1/2 truncate pl-1 text-[10px] font-medium leading-none text-muted-foreground"
                        style={{ left: m.leftPx }}
                        title={m.label}
                      >
                        {m.label}
                      </span>
                    ))}
                  </TimelineGrid>
                </div>

                <SortableContext items={sortableTaskIds} strategy={verticalListSortingStrategy}>
                  {sortedTasks.map((task, i) => (
                    <SortableGanttGridRow
                      key={task.id}
                      task={task}
                      index={i}
                      model={model}
                      expandedId={expandedId}
                      setExpandedId={setExpandedId}
                      onUpdate={onUpdate}
                      onDelete={confirmDeleteTask}
                      readOnly={ro}
                    />
                  ))}
                </SortableContext>
              </div>
            </div>
          </DndContext>
        )}
      </div>

      <Sheet
        open={expandedId !== null}
        onOpenChange={(open) => {
          if (!open) setExpandedId(null);
        }}
      >
        <SheetContent
          side={taskSheetSide}
          className={cn(
            "flex gap-0 overflow-hidden p-0 sm:max-w-lg",
            taskSheetSide === "right" && "h-full w-full flex-col border-l",
            taskSheetSide === "bottom" && "max-h-[90vh] flex-col rounded-t-xl border-t",
          )}
        >
          {expandedTask ? (
            <>
              <SheetHeader className="shrink-0 space-y-1 border-b px-4 py-4 pr-12 text-left">
                <SheetTitle className="text-base">Параметры задачи</SheetTitle>
                <SheetDescription asChild>
                  <p className="line-clamp-3 text-left text-muted-foreground text-sm">
                    <span className="font-medium text-foreground">{expandedTask.title}</span>
                    <span className="text-muted-foreground">
                      {" "}
                      · {formatRuDateRange(expandedTask.start, expandedTask.end)}
                    </span>
                  </p>
                </SheetDescription>
              </SheetHeader>
              <div
                ref={setTaskSheetScrollEl}
                className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
              >
                <TaskExpandedPanel
                  task={expandedTask}
                  projectId={projectId}
                  documents={documents}
                  onDocumentsChange={onDocumentsChange}
                  onUpdate={(patch) => onUpdate(expandedTask.id, patch)}
                  onDeleteRow={() => {
                    const title = (expandedTask.title || "").trim() || "эту задачу";
                    if (typeof window !== "undefined") {
                      const ok = window.confirm(
                        `Удалить задачу «${title}»?\n\nСтрока Ганта и связанные даты/результаты будут удалены. Действие необратимо.`,
                      );
                      if (!ok) return;
                    }
                    onDelete(expandedTask.id);
                    setExpandedId(null);
                  }}
                  readOnly={ro}
                  className="border-t-0"
                  popoverPortalContainer={taskSheetScrollEl}
                />
              </div>
            </>
          ) : (
            <div className="p-4 text-sm text-muted-foreground">Задача не найдена.</div>
          )}
        </SheetContent>
      </Sheet>

      {!embedded ? (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {!ro ? (
            <button
              type="button"
              onClick={handleAdd}
              className="flex items-center gap-1.5 rounded-md bg-[var(--nn-blue)] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[var(--nn-blue-dark)]"
            >
              <Plus size={13} /> Добавить задачу
            </button>
          ) : null}
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            {Object.entries(STATUS_COLORS).map(([s, c]) => (
              <span key={s} className="flex items-center gap-1">
                <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: c }} />
                {STATUS_LABELS[s as EditableGanttTask["status"]]}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
